import { useMemo } from "react";
import { Box, makeStyles, Grid, Theme, Paper, useTheme, useMediaQuery } from "@material-ui/core";
import { Quote } from "../../../api/Quote/Quote";
import { colors } from "swyft-react-core";
import { EventSlot } from "../../../api/EventSlot/EventSlot";
import { addMonths, format } from "date-fns";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import { QuoteHr } from "../../quotePage/components/QuoteHr";
import { enGB } from "date-fns/locale";
import { Calendar } from "@axel-dev/react-nice-dates";
import "@axel-dev/react-nice-dates/build/style.css";
import CountdownTimer from "../../../components/CountDownTimer";
import { utcToLocal } from "../../../app/utils";
import TimerIcon from "@material-ui/icons/Timer";
import { TF } from "../../../components/typography/TF";

interface Props {
  quote: Quote;
  eventSlots: EventSlot[] | null;
  selectedEventSlot: EventSlot | null;
  onLockEventSlot: (eventSlot: EventSlot) => Promise<boolean>;
  onNext: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    "& .nice-dates-day:before": {
      backgroundColor: colors.secondary
    },
    "& .nice-dates-day:after": {
      border: "2px solid" + colors.secondary
    },
    "& .nice-dates-day.-outside": {
      color: "#333"
    },
    "& .nice-dates-day.-disabled": {
      color: "#ddd"
    },
    [theme.breakpoints.down("xs")]: {
      "& .nice-dates-day_month": {
        top: 0
      }
    }
  },
  boxDate: {
    display: "flex",
    alignItems: "center"
  },
  icon: {
    verticalAlign: "sub",
    marginRight: theme.spacing(1)
  }
}));

interface CalendarViewProp {
  date?: Date;
  onChangeDate: (date: Date | null) => void;
  availableDates: Date[];
}

const compareDate = (dayLeft: Date, dayRight: Date) => {
  return (
    dayLeft.getDate() === dayRight.getDate() &&
    dayLeft.getMonth() === dayRight.getMonth() &&
    dayLeft.getFullYear() === dayRight.getFullYear()
  );
};

const CalendarView = ({ date, onChangeDate, availableDates }: CalendarViewProp) => {
  const modifiers = {
    selected: (day: Date): boolean => {
      return date ? compareDate(date, day) : false;
    },
    disabled: (day: Date | null): boolean => {
      if (!day) {
        return true;
      }

      return !availableDates.some((item) => compareDate(new Date(item), day));
    }
  };

  return (
    <Calendar
      month={date}
      locale={enGB}
      minimumDate={new Date()}
      maximumDate={addMonths(new Date(), 6)}
      onDayClick={onChangeDate}
      modifiers={modifiers}
    />
  );
};

export const QuoteAddonWizardSchedule = ({ quote, eventSlots, onLockEventSlot, selectedEventSlot, onNext }: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const selectedDate = useMemo(
    () => (selectedEventSlot?.startDate ? new Date(selectedEventSlot?.startDate) : undefined),
    [selectedEventSlot]
  );

  if (!eventSlots) {
    return null;
  }

  const availableDates = eventSlots.map((i) => i.startDate);

  const handleChange = async (day: Date | null) => {
    if (!day) {
      return;
    }

    const eventSlot = eventSlots.find((item) => compareDate(new Date(item.startDate), day));

    if (eventSlot) {
      await onLockEventSlot(eventSlot);
    }
  };

  const dateSection = selectedEventSlot && (
    <Box className={classes.boxDate}>
      <EventAvailableIcon color="secondary" className={classes.icon} />
      <TF variant="body2" component="span">
        Selected Installation Date is
      </TF>
      <Box ml={1}>
        <TF variant="h2" component="span" color="secondary">
          {format(new Date(selectedEventSlot.startDate), "d")}
        </TF>
        <TF variant="body2" bold component="span">
          &nbsp;{format(new Date(selectedEventSlot.startDate), "MMM")}
        </TF>
      </Box>
    </Box>
  );

  const countDownSection = selectedEventSlot && quote.eventSlotExpire && (
    <Box display="flex" flexDirection={isSmall ? "column" : "row"} alignItems={isSmall ? "start" : "center"}>
      <Box width={0.35} display="flext" alignItems="center">
        <TimerIcon color="secondary" className={classes.icon} />
        <TF variant="h1" component="span" bold>
          <CountdownTimer date={utcToLocal(quote.eventSlotExpire)} />
        </TF>
      </Box>
      <Box>
        <TF variant="caption" color="storeGrey">
          This timer shows how long we'll be able to hold this date during booking. If time runs out, other customers
          will have a chance to select this date.
        </TF>
      </Box>
    </Box>
  );

  return (
    <>
      <Paper className={classes.root} elevation={2}>
        <Box px={4} py={3}>
          <Box>
            <CalendarView date={selectedDate} onChangeDate={handleChange} availableDates={availableDates} />
          </Box>
          <QuoteHr mb={1} />
          <Box color={colors.darkGrey}>
            <TF variant="body2" color="storeGrey">
              Your engineer will arrive before 10.00am. If there are any changes to this time, we will let you know as
              soon as possible
            </TF>
            <Box mt={2}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {dateSection}
                </Grid>
                <Grid item xs={12}>
                  {countDownSection}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  );
};
